import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, Mic, Send, X, RefreshCw, PhoneCall, MicOff, PhoneOff, Waves, Mail, Phone, User } from 'lucide-react';
import emailjs from '@emailjs/browser';
import './styles.css'; 
import ReactGA from 'react-ga4';
import { QurveSDK } from '../utils/qurve-sdk';
import { registerVoiceCall } from '../services/voiceApi';

// Base URL configuration based on environment
const getBaseUrl = () => {
  switch (process.env.REACT_APP_ENV) {
    case 'production':
      return 'https://api.qurve.ai/api/';
    case 'staging':
      return 'https://staging.qurve.ai/api/';
    default:
      return 'https://staging.qurve.ai/api/'; // Default to staging
  }
};

// Google Analytics Configuration
const gaTrackingId = 'G-LGDH2WRMJ7';

// Google Analytics Functions
const initializeGA = (measurementId) => {
  return new Promise((resolve, reject) => {
    try {
      // Create script element
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;

      // Initialize dataLayer
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        window.dataLayer.push(arguments);
      }
      window.gtag = gtag;
      gtag('js', new Date());

      // Wait for script to load before initializing
      script.onload = () => {
        gtag('config', measurementId);
        resolve();
      };

      script.onerror = (error) => {
        reject(new Error('Failed to load Google Analytics: ' + error));
      };

      // Add script to document
      document.head.appendChild(script);
    } catch (error) {
      reject(error);
    }
  });
};

const trackEvent = (eventName, eventParams = {}) => {
  try {
    if (window.gtag) {
      window.gtag('event', eventName, {
        ...eventParams,
        timestamp: new Date().toISOString(),
      });
    }
  } catch (error) {
    console.warn('GA Event tracking failed:', error);
  }
};

const nanoid = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

// Constants
const COLOR_PALETTES = {
  default: {
    primary: '#4f46e5',
    secondary: '#6366f1',
    background: '#ffffff',
    text: '#1f2937',
    accent: '#ef4444'
  },
  dark: {
    primary: '#3b82f6',
    secondary: '#10b981',
    background: '#111827',
    text: '#f9fafb',
    accent: '#f43f5e'
  },
  pastel: {
    primary: '#8b5cf6',
    secondary: '#22d3ee',
    background: '#f5f5f5',
    text: '#374151',
    accent: '#fbbf24'
  }
};

const FONT_FAMILIES = {
  default: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
  modern: 'Poppins, system-ui, sans-serif',
  classic: 'Roboto, system-ui, sans-serif'
};

// Enhanced Navigation utilities - COMPLETELY REWRITTEN
const detectFramework = () => {
  if (typeof window === 'undefined') return null;
  
  // Check for Next.js
  if (window.next || window.__NEXT_DATA__ || window.__NEXT_ROUTER__) {
    return 'nextjs';
  }
  
  // Check for Vue.js
  if (window.Vue || window.__VUE__ || (window.$nuxt && window.$nuxt.$router)) {
    return 'vue';
  }
  
  return 'vanilla';
};

// Universal navigation function - FIXED VERSION
const createSafeNavigate = () => {
  const framework = detectFramework();
  
  return async (path, options = {}) => {
    try {
      console.log(`Attempting navigation to: ${path} using ${framework}`);
      
      let navigationSuccess = false;
      
      switch (framework) {
        case 'nextjs':
          navigationSuccess = await handleNextJSNavigation(path, options);
          break;
        
        case 'vue':
          navigationSuccess = await handleVueNavigation(path, options);
          break;
        
        default:
          navigationSuccess = await handleVanillaNavigation(path, options);
          break;
      }
      
      return navigationSuccess;
    } catch (error) {
      console.warn('Navigation failed:', error);
      return handleFallbackNavigation(path, options);
    }
  };
};

// Next.js navigation handler - FIXED
const handleNextJSNavigation = async (path, options = {}) => {
  try {
    // Method 1: Try Next.js router through window.next
    if (typeof window !== 'undefined' && window.next?.router) {
      if (options.replace) {
        await window.next.router.replace(path);
      } else {
        await window.next.router.push(path);
      }
      return true;
    }
    
    // Method 2: Try accessing router through __NEXT_ROUTER__
    if (typeof window !== 'undefined' && window.__NEXT_ROUTER__) {
      const router = window.__NEXT_ROUTER__;
      if (options.replace) {
        await router.replace(path);
      } else {
        await router.push(path);
      }
      return true;
    }
    
    // Method 3: Use History API for Next.js
    return await handleHistoryAPINavigation(path, options);
  } catch (error) {
    console.warn('Next.js navigation failed:', error);
    return await handleHistoryAPINavigation(path, options);
  }
};

// Vue navigation handler - FIXED
const handleVueNavigation = async (path, options = {}) => {
  try {
    // Try Vue Router through $router
    if (typeof window !== 'undefined' && window.$router) {
      if (options.replace) {
        await window.$router.replace(path);
      } else {
        await window.$router.push(path);
      }
      return true;
    }
    
    // Try Nuxt.js router
    if (typeof window !== 'undefined' && window.$nuxt && window.$nuxt.$router) {
      if (options.replace) {
        await window.$nuxt.$router.replace(path);
      } else {
        await window.$nuxt.$router.push(path);
      }
      return true;
    }
    
    // Fallback to History API
    return await handleHistoryAPINavigation(path, options);
  } catch (error) {
    console.warn('Vue navigation failed:', error);
    return await handleHistoryAPINavigation(path, options);
  }
};

// History API navigation handler - FIXED
const handleHistoryAPINavigation = async (path, options = {}) => {
  return new Promise((resolve) => {
    try {
      if (typeof window !== 'undefined' && window.history) {
        if (options.replace) {
          window.history.replaceState(options.state || null, '', path);
        } else {
          window.history.pushState(options.state || null, '', path);
        }
        
        // Dispatch events to notify other components
        const popStateEvent = new PopStateEvent('popstate', { 
          state: options.state 
        });
        window.dispatchEvent(popStateEvent);
        
        const customEvent = new CustomEvent('navigation', {
          detail: { path, options }
        });
        window.dispatchEvent(customEvent);
        
        resolve(true);
      } else {
        resolve(false);
      }
    } catch (error) {
      console.warn('History API navigation failed:', error);
      resolve(false);
    }
  });
};

// Vanilla navigation handler
const handleVanillaNavigation = async (path, options = {}) => {
  return await handleHistoryAPINavigation(path, options);
};

// Fallback navigation handler
const handleFallbackNavigation = (path, options = {}) => {
  try {
    // Last resort: use window.location
    if (typeof window !== 'undefined') {
      if (options.replace) {
        window.location.replace(path);
      } else {
        window.location.href = path;
      }
      return true;
    }
    return false;
  } catch (error) {
    console.error('All navigation methods failed:', error);
    return false;
  }
};

// Create the navigation function
const safeNavigate = createSafeNavigate();

// Session Management Functions
const createNewSession = () => {
  const sessionId = nanoid();
  return sessionId;
};

// API Services
const apiServices = {
  async makeQuestionRequest(apiKey, sessionId, question, baseUrl,metadata) {
    
    function getCookie(name) {
      let cookieValue = null;
      if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
          const cookie = cookies[i].trim();
          if (cookie.substring(0, name.length + 1) === (name + '=')) {
            cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
            break;
          }
        }
      }
      return cookieValue;
    }
    
    const csrftoken = getCookie('csrftoken');
    const QUESTION_URL = `${baseUrl}cortex/ragchat_b/`;
    
    const headers = {
      'Content-Type': 'application/json',
      'X-API-KEY': apiKey,
      'Accept': 'application/json',
      'X-CSRFToken': csrftoken,
    };

    try {
      const requestBody = {
        text: question,
        session_id: sessionId,
        stream: true
      };
      
      if (metadata) {
        requestBody.metadata = metadata;
      }
      
      const response = await fetch(QUESTION_URL, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
        credentials: 'include'
      });
      

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },

  async registerVoiceCall(apiKey, sessionId, baseUrl, metadata = null) {
    return registerVoiceCall(baseUrl, { apiKey }, {
      originating_domain: window.location.hostname,
      session_id: sessionId,
      metadata: metadata || {},
    });
  }

};

// Component: NavigationControl - Shared between Chat and Voice
const NavigationControl = ({ allowNavigation, setAllowNavigation, mergedColorPalette }) => (
  <div 
    className="navigation-control-container"
    style={{
      borderColor: `${mergedColorPalette.secondary}15`,
      backgroundColor: mergedColorPalette.background
    }}
  >
    <div 
      className="navigation-checkbox-wrapper"
      style={{ color: mergedColorPalette.text }}
    >
      <div className="navigation-checkbox-container">
        <input
          type="checkbox"
          id="navigation-toggle"
          checked={allowNavigation}
          onChange={(e) => setAllowNavigation(e.target.checked)}
          className="navigation-checkbox-input"
          style={{
            backgroundColor: mergedColorPalette.primary,
            borderColor: `${mergedColorPalette.secondary}30`
          }}
        />
        {allowNavigation && (
          <svg
            className="navigation-checkbox-checkmark"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.5 6L5 8.5L9.5 4"
              stroke={mergedColorPalette.background}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
      <label 
        htmlFor="navigation-toggle" 
        className="navigation-checkbox-label"
      >
        Allow Navigation
      </label>
    </div>
  </div>
);

// Component: ConfirmationModal
const ConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  confirmText, 
  cancelText, 
  colorPalette 
}) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-backdrop" onClick={onClose} />
      <div 
        className="modal-container"
        style={{ 
          backgroundColor: colorPalette.background,
          '--modal-background-color': colorPalette.background 
        }}
      >
        <h3 
          className="modal-title"
          style={{ color: colorPalette.text }}
        >
          {title}
        </h3>
        <p 
          className="modal-message"
          style={{ color: colorPalette.text }}
        >
          {message}
        </p>
        <div className="modal-actions">
          <button
            onClick={onClose}
            className="modal-button modal-cancel-button"
            style={{ color: colorPalette.text }}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className="modal-button modal-confirm-button"
            style={{ backgroundColor: colorPalette.accent }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// Component: ContactForm
const ContactForm = ({ onSubmit, colorPalette }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ name, email, phone });
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="thank-you-message" style={{
        backgroundColor: colorPalette.background,
        color: colorPalette.text
      }}>
        <h2 className="thank-you-title" style={{ color: colorPalette.primary }}>
          Thank You!
        </h2>
        <p>We will reach out to you shortly.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="contact-form">
      <div className="form-group">
        <div className="input-container">
          <User 
            color={colorPalette.primary} 
            className="input-icon" 
          />
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="form-input"
            style={{
              borderColor: `${colorPalette.secondary}30`,
              backgroundColor: colorPalette.background,
              color: colorPalette.text
            }}
            placeholder="Full name"
          />
        </div>
      </div>

      <div className="form-group">
        <div className="input-container">
          <Mail 
            color={colorPalette.primary} 
            className="input-icon" 
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="form-input"
            style={{
              borderColor: `${colorPalette.secondary}30`,
              backgroundColor: colorPalette.background,
              color: colorPalette.text
            }}
            placeholder="Email id"
          />
        </div>
      </div>

      <div className="form-group">
        <div className="input-container">
          <Phone 
            color={colorPalette.primary} 
            className="input-icon" 
          />
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
            className="form-input"
            style={{
              borderColor: `${colorPalette.secondary}30`,
              backgroundColor: colorPalette.background,
              color: colorPalette.text
            }}
            placeholder="Phone number (optional)"
          />
        </div>
      </div>

      <button
        type="submit"
        className="submit-button"
        style={{
          backgroundColor: colorPalette.primary,
          color: colorPalette.background
        }}
      >
        Submit 
      </button>
    </form>
  );
};

// Component: ChatContent
const ChatContent = ({ 
    messages, 
    inputMessage, 
    setInputMessage, 
    sendMessage, 
    isLoading, 
    messagesEndRef, 
    mergedColorPalette,
    allowNavigation,
    setAllowNavigation,
    showNavigationControl
  }) => (
    <div className="combined-chat-container">
      {/* Navigation control in header - only show if showNavigationControl is true */}
      {showNavigationControl && (
        <NavigationControl
          allowNavigation={allowNavigation}
          setAllowNavigation={setAllowNavigation}
          mergedColorPalette={mergedColorPalette}
        />
      )}
      
      {/* Rest of the component remains the same */}
      <div className="combined-messages-container">
        {messages.map((message, index) => (
          <div 
            key={message.id} 
            className={`combined-message ${message.sender}`}
            style={{ 
              animationDelay: `${index * 100}ms`,
              opacity: 0,
              animation: `fadeIn 0.3s ease-in-out ${index * 100}ms forwards`
            }}
          >
            <div 
              className="combined-message-content"
              style={{
                backgroundColor: message.sender === 'user' 
                  ? mergedColorPalette.primary 
                  : `${mergedColorPalette.secondary}15`,
                color: message.sender === 'user' 
                  ? mergedColorPalette.background
                  : mergedColorPalette.text,
                animation: `bounce 0.6s ease-in-out ${index * 100}ms`
              }}
            >
              {message.text || (
                <div className="typing-indicator" style={{ display: 'flex', gap: '4px', padding: '8px 4px' }}>
                  {[...Array(3)].map((_, i) => (
                    <div 
                      key={i} 
                      style={{ 
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        backgroundColor: message.sender === 'user' 
                          ? mergedColorPalette.background 
                          : mergedColorPalette.text,
                        animation: 'waveAnimation 1.2s ease-in-out infinite',
                        animationDelay: `${i * 0.2}s`,
                        transformOrigin: 'center bottom'
                      }} 
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="combined-input-container">
        <input
          type="text"
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && !isLoading && inputMessage.trim() && sendMessage()}
          placeholder="Type your message..."
          className="combined-input"
          style={{
            borderColor: `${mergedColorPalette.secondary}30`,
            color: mergedColorPalette.text,
            backgroundColor: mergedColorPalette.background,
          }}
        />
        <button
          onClick={sendMessage}
          disabled={!inputMessage.trim() || isLoading}
          className="combined-send-button"
          style={{
            backgroundColor: mergedColorPalette.primary,
            opacity: (!inputMessage.trim() || isLoading) ? 0.5 : 1
          }}
        >
          <Send size={16} style={{ color: mergedColorPalette.background }} />
        </button>
      </div>
    </div>
  );

// Component: VoiceContent - Updated with Navigation Control
const VoiceContent = ({ 
  clientName,
  showContactForm,
  handleContactSubmit,
  callOngoing,
  isInitializing,
  startCall,
  isMuted,
  toggleMute,
  endCall,
  error,
  mergedColorPalette,
  isFreeTrial,
  allowNavigation,
  setAllowNavigation,
  showNavigationControl
}) => (
  <div className="voice-container">
    {/* Navigation control in voice mode - only show if showNavigationControl is true */}
    {showNavigationControl && (
      <NavigationControl
        allowNavigation={allowNavigation}
        setAllowNavigation={setAllowNavigation}
        mergedColorPalette={mergedColorPalette}
      />
    )}

    {(!isFreeTrial) ? (
      <>
        <div 
          className="trial-message"
          style={{
            backgroundColor: `${mergedColorPalette.secondary}10`,
            color: mergedColorPalette.text,
            borderLeft: `4px solid ${mergedColorPalette.primary}`
          }}
        >
          Free trial ended. Fill out this form for a trial extension
        </div>
        <ContactForm 
          onSubmit={handleContactSubmit}
          colorPalette={mergedColorPalette}
        />
      </>
    ) : (
      <>
        <div className="client-header">
          <div className="client-title">
            <Waves 
              className="client-icon" 
              style={{ color: mergedColorPalette.primary }} 
            />
            <h2 
              className="client-name"
              style={{ color: mergedColorPalette.text }}
            >
              {clientName}
            </h2>
          </div>
          <p className="client-subtitle">Voice Assistant</p>
        </div>

        <div className="avatar-container">
          <div 
            className="avatar-circle"
            style={{ 
              backgroundColor: callOngoing ? mergedColorPalette.primary : `${mergedColorPalette.text}10`,
              color: callOngoing ? mergedColorPalette.background : mergedColorPalette.text
            }}
          >
            {!callOngoing ? (
              <Mic className="mic-icon" />
            ) : (
              <div className="wave-container">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="wave-bar"
                    style={{
                      backgroundColor: `${mergedColorPalette.background}50`,
                      animationDelay: `${i * 100}ms`
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="controls-container">
          {!callOngoing ? (
            <button
              onClick={startCall}
              disabled={isInitializing}
              className="start-call-button"
              style={{
                backgroundColor: mergedColorPalette.primary,
                color: mergedColorPalette.background
              }}
            >
              {isInitializing ? (
                <>
                  <div className="loading-spinner" />
                  Initializing...
                </>
              ) : (
                <>
                  <PhoneCall className="button-icon" />
                  Start Call
                </>
              )}
            </button>
          ) : (
            <>
              <button
                onClick={toggleMute}
                className="mute-button"
                style={{
                  backgroundColor: isMuted ? mergedColorPalette.secondary : `${mergedColorPalette.text}10`,
                  color: isMuted ? mergedColorPalette.background : mergedColorPalette.text
                }}
              >
                {isMuted ? <Mic className="button-icon" /> : <MicOff className="button-icon" />}
                {isMuted ? 'Unmute' : 'Mute'}
              </button>
              <button
                onClick={endCall}
                className="end-call-button"
                style={{
                  backgroundColor: mergedColorPalette.accent,
                  color: mergedColorPalette.background
                }}
              >
                <PhoneOff className="button-icon" />
                End
              </button>
            </>
          )}
        </div>
      </>
    )}
  </div>
);

const Footer = ({ colorPalette }) => (
  <div
    className="footer"
    style={{
      borderTop: `1px solid ${colorPalette.secondary}15`,
      textAlign: 'center',
      fontSize: '12px',
      color: `${colorPalette.text}80`,
      backgroundColor: colorPalette.background,
      width: '100%',
      margin: '0 0 8px 0',
      border: 'none'
    }}
  >
    Powered by Qurve AI
  </div>
);

// Main Component
const CombinedAIWidget = ({
  apiKey,
  env = 'staging',
  ga = false,
  clientName = 'AI Assistant',
  metadata = null,
  onMessageSend,
  onConversationReset,
  onCallStart,
  onCallEnd,
  environment = 'development',
  colorPalette = {},
  font = 'default',
  maxMessages = 20,
  callDurationLimit = 30 * 60 * 1000,
  navigationScrollAmount = 200,
  showNavigationControl = true,
  attentionMessage = '' 
}) => {
  const [isInIframe, setIsInIframe] = useState(false);
  const [widgetOpen, setWidgetOpen] = useState(true);
  const [activeMode, setActiveMode] = useState('voice');
  const [sessionId, setSessionId] = useState(createNewSession);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [callOngoing, setCallOngoing] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [showContactForm, setShowContactForm] = useState(true);
  const [showEndCallModal, setShowEndCallModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [gaInitialized, setGaInitialized] = useState(false);
  const remainingCallTimeRef = useRef(0);
  const isFreeTrial = useRef(true);
  const callTimerRef = useRef(null);
  const [firstMessageUrl, setFirstMessageUrl] = useState(null);
  const [allowNavigation, setAllowNavigation] = useState(false);
  
  const messagesEndRef = useRef(null);
  const qurveSdkRef = useRef(null);
  const mountedRef = useRef(true);
  const voiceMetadataHandlerRef = useRef(null);

  const mergedColorPalette = { ...COLOR_PALETTES.default, ...colorPalette };
  const selectedFont = FONT_FAMILIES[font] || FONT_FAMILIES.default;
  
  // Get base URL based on environment
  const baseUrl = getBaseUrl();

  // Initialize Google Analytics only if ga prop is true
  useEffect(() => {
    if (ga && gaTrackingId && !gaInitialized) {
      initializeGA(gaTrackingId)
        .then(() => {
          setGaInitialized(true);
          console.log('Google Analytics initialized successfully');
        })
        .catch(error => {
          console.warn('Google Analytics initialization failed:', error);
        });
    }
  }, [ga, gaInitialized]);

  const handleNavigationAndScroll = async (url) => {
    try {
      if (url !== '/faq/manual') {
        console.log(`Attempting navigation to: ${url}`);
        
        // STEP 1: Navigate first
        const navigationSuccess = await safeNavigate(url, { replace: false });
        
        if (navigationSuccess) {
          console.log('Successfully navigated to:', url);
          
          // STEP 2: Wait for navigation to complete, then scroll
          // Use requestAnimationFrame to ensure DOM is updated
          requestAnimationFrame(() => {
            setTimeout(() => {
              try {
                window.scrollBy({
                  top: navigationScrollAmount,
                  behavior: 'smooth'
                });
                console.log('Scrolled by:', navigationScrollAmount);
              } catch (scrollError) {
                console.warn('Scroll failed:', scrollError);
              }
            }, 1000); // Increased delay to ensure navigation completes
          });
        } else {
          console.warn('Navigation failed for:', url);
        }
      }
    } catch (error) {
      console.error("Invalid URL or navigation failed:", firstMessageUrl, error);
    }
  };

  // FIXED: Navigation effect - navigate first, then scroll
  useEffect(() => {
    if (firstMessageUrl != null && allowNavigation && showNavigationControl) {
      let url = firstMessageUrl.startsWith("http")
            ? new URL(firstMessageUrl)
            : new URL(firstMessageUrl, window.location.origin);

      if(allowNavigation) { handleNavigationAndScroll(url.pathname);}
    }
  }, [firstMessageUrl, navigationScrollAmount, showNavigationControl]);

  useEffect(() => {
    if (callOngoing) {
      // Initialize remaining time
      remainingCallTimeRef.current = callDurationLimit;
      console.log(callDurationLimit)

      // Start the interval timer
      callTimerRef.current = setInterval(() => {
        // Directly modify the ref value
        remainingCallTimeRef.current -= 1000;

        // Check if time is up
        if (remainingCallTimeRef.current <= 1000) {
          if (callTimerRef.current) {
            clearInterval(callTimerRef.current);
            endCall(true);
          }
        }
      }, 1000);

      // Cleanup function
      return () => {
        if (callTimerRef.current) {
          clearInterval(callTimerRef.current);
        }
      };
    }
  }, [callOngoing, callDurationLimit]);

  // Handle page refresh/unload
  useEffect(() => {
    const handleBeforeUnload = () => createNewSession();
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffect(() => {
    try {
      setIsInIframe(window.self !== window.top);
    } catch (e) {
      setIsInIframe(true);
    }
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 600);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearInterval(callTimerRef.current);
      qurveSdkRef.current?.abortCall().catch(error => console.error('Error ending call:', error));
      qurveSdkRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (isMobile) {
      setWidgetOpen(false);
    }
  }, [isMobile]);

  
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleMetadata = (metadataReceived) => {
    if (
      allowNavigation && showNavigationControl &&
      metadataReceived &&
      metadataReceived.metadata &&
      metadataReceived.metadata.source
    ) {
      const sourceUrl = metadataReceived.metadata.source;
      const pathOnly = new URL(sourceUrl, window.location.origin).pathname; // Extracts only the path
      console.log('Path received in VoiceChat:', pathOnly);
      handleNavigationAndScroll(pathOnly);
    }
  };
  

  voiceMetadataHandlerRef.current = handleMetadata;

  const initSdk = async (currentSessionId) => {
    if (qurveSdkRef.current) return;
    setIsInitializing(true);
    setError(null);
    let started = false;
    const sdk = new QurveSDK({
      baseUrl,
      apiKey,
      sessionId: currentSessionId,
      metadata: metadata || {},
      registerCall: () => apiServices.registerVoiceCall(apiKey, currentSessionId, baseUrl, metadata),
      onMetadata: showNavigationControl ? event => voiceMetadataHandlerRef.current?.(event) : undefined,
      onCallStart: () => {
        if (!mountedRef.current || qurveSdkRef.current !== sdk) return;
        started = true;
        setCallOngoing(true);
        onCallStart?.();
      },
      onCallEnd: () => {
        if (!mountedRef.current || qurveSdkRef.current !== sdk) return;
        qurveSdkRef.current = null;
        setCallOngoing(false);
        setIsMuted(false);
        clearInterval(callTimerRef.current);
        callTimerRef.current = null;
        if (started) onCallEnd?.();
      },
      onError: error => {
        if (mountedRef.current && qurveSdkRef.current === sdk) setError(`Call failed: ${error.message}`);
      },
    });
    qurveSdkRef.current = sdk;
    try {
      await sdk.startCall();
    } finally {
      if (mountedRef.current) setIsInitializing(false);
    }
  };

  const processStreamedResponse = async (response, messageId) => {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullText = '';
    let isFirstChunk = true;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n').filter(line => line.trim());

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const jsonData = JSON.parse(line.slice(5));
            if (jsonData.chunk !== undefined) {
              fullText += jsonData.chunk;
              
              // Check for URL in first chunk - only if navigation control is enabled
              if (isFirstChunk && allowNavigation && showNavigationControl) {
                isFirstChunk = false;
                const urlMatch = jsonData.source;
                console.log(urlMatch)
                if (urlMatch) {
                  setFirstMessageUrl(urlMatch);
                }
              }

              setMessages(prev => 
                prev.map(msg => 
                  msg.id === messageId ? { ...msg, text: fullText } : msg
                )
              );
              
              if (jsonData.done) break;
            }
          }
        }
      }
    } catch (error) {
      console.error('Stream error:', error);
      throw error;
    }
    return fullText;
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !apiKey) return;

    const userMessageId = nanoid();
    const aiMessageId = nanoid();

    setMessages(prev => [
      ...prev,
      { id: userMessageId, sender: 'user', text: inputMessage },
      { id: aiMessageId, sender: 'ai', text: '' }
    ]);

    setInputMessage('');
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiServices.makeQuestionRequest(apiKey, sessionId, inputMessage, baseUrl , metadata);
      await processStreamedResponse(response, aiMessageId);
      onMessageSend?.(inputMessage);
    } catch (err) {
      setError('Message failed. Try again.');
      setMessages(prev => prev.filter(msg => msg.id !== aiMessageId));
    } finally {
      setIsLoading(false);
    }
  };

  const handleModeSwitch = (newMode) => {
    if (callOngoing) {
      setPendingAction({ type: 'switchMode', payload: newMode });
      setShowEndCallModal(true);
    } else {
      setActiveMode(newMode);
    }
  };

  const handleWidgetClose = () => {
    if (callOngoing) {
      setPendingAction({ type: 'closeWidget' });
      setShowEndCallModal(true);
    } else {
      setWidgetOpen(false);
    }
  };

  const handleConfirmAction = () => {
    if (pendingAction) {
      endCall();
      if (pendingAction.type === 'switchMode') {
        setActiveMode(pendingAction.payload);
      } else if (pendingAction.type === 'closeWidget') {
        setWidgetOpen(false);
      }
      setPendingAction(null);
    }
    setShowEndCallModal(false);
  };

  const startCall = async () => {
    if (qurveSdkRef.current || isInitializing) return;
    const newSessionId = createNewSession();
    setSessionId(newSessionId);
    
    // Track call start event only if GA is enabled and initialized
    if (ga && gaInitialized) {
      trackEvent('start_call', {
        action: `start_call ${clientName}`,
        category: 'voice_interaction',
        label: 'voice_call_initiated'
      });
      
      ReactGA.event({
        category: 'User',
        action: 'AI widget start call',
        label: 'AI widget call started'
      });
    }
    
    try {
      await initSdk(newSessionId);
    } catch (error) {
      // Track call error event only if GA is enabled and initialized
      if (ga && gaInitialized) {
        trackEvent('call_error', {
          action: `call_error ${error.message}`,
          category: 'voice_interaction',
          label: error.message
        });
      }
      
      if (mountedRef.current) {
        setError(`Call failed: ${error.message}`);
        setCallOngoing(false);
      }
    }
  };

  const endCall = (freeTrialEnded = false) => {
    if (freeTrialEnded && remainingCallTimeRef.current <= 1000) {
      isFreeTrial.current = false;
      emailjs.init('di5DjQBXJiqAmsbWq');
    }
    qurveSdkRef.current?.abortCall().catch(error => {
      if (mountedRef.current) setError(`End call failed: ${error.message}`);
    });
  };

  const toggleMute = () => {
    if (qurveSdkRef.current) {
      try {
        isMuted ? qurveSdkRef.current.unmuteCall() : qurveSdkRef.current.muteCall();
        setIsMuted(!isMuted);
      } catch (error) {
        setError(`Mute failed: ${error.message}`);
      }
    }
  };

  const handleContactSubmit = async (contactData, setError, setShowContactForm) => {
    try {
      // Create the template parameters
      const templateParams = {
        from_name: contactData.name,
        from_email: contactData.email,
        phone: contactData.phone || 'Not provided',
        to_name: 'Qurve AI Team',
        message: `New contact submission from ${contactData.name}
          \nEmail: ${contactData.email}
          \nPhone: ${contactData.phone || 'Not provided'}`
      };
  
      // Send email using EmailJS
      const response = await emailjs.send(
        'service_an9kqqj',
        'template_u891fuq',
        templateParams
      );
  
      if (response.status === 200) {
        setError('');
        setShowContactForm(false);
        return true;
      } else {
        throw new Error('Failed to send contact information');
      }
    } catch (error) {
      console.error('EmailJS Error:', error);
      setError('Failed to submit contact information');
      return false;
    }
  };

  if (isInIframe) return null;

  return (
    <>
      <div className="combined-widget-container" style={{ fontFamily: selectedFont }}>
        {!widgetOpen ? (
          <div className="widget-attention-container">
          {/* Attention message with arrow */}
          <div
            className="combined-widget-button"
            style={{
              backgroundColor: "transparent",
              color: mergedColorPalette.aiprimary
            }}
          >
          { attentionMessage &&
          <div 
            className="widget-attention-message"
            style={{
              backgroundColor: mergedColorPalette.aibackground,
              color: mergedColorPalette.aiprimary,
              position: 'absolute',
              bottom: '60px',
              right: '20px',
              padding: '8px 12px',
              borderRadius: '8px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              maxWidth: '180px',
              fontWeight: '500',
              opacity: '0.95',
              animation: 'pulse 2s infinite',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease',
              zIndex: '49'
            }}
          >
            {attentionMessage}
            {/* Downward arrow */}
            <div 
              style={{
                position: 'absolute',
                bottom: '-8px',
                right: '20px',
                width: '0',
                height: '0',
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderTop: `8px solid ${mergedColorPalette.aibackground}`
              }}
            ></div>
          </div>
          }
          <div className="combined-widget-icon"
          onClick={() => {
            setWidgetOpen(true);
            // Track widget open event only if GA is enabled and initialized
            if (ga && gaInitialized) {
              ReactGA.event({
                category: 'User',
                action: 'AI widget',
                label: 'AI widget opened'
              });
            }
          }}
            style={{
              backgroundColor: mergedColorPalette.aibackground,
              color: mergedColorPalette.aiprimary
            }}
          >
            {activeMode === 'chat' ? (
              <MessageCircle className="combined-icon" />
            ) : (
              <PhoneCall className="combined-icon" />
            )}
            </div>
          </div>
        </div>
        ) : (
          <div 
            className="combined-widget-content"
            style={{ 
              backgroundColor: mergedColorPalette.background,
              borderColor: `${mergedColorPalette.secondary}15`,
              height: activeMode === 'chat' ? (isMobile ? '450px' : '480px') : 'auto'
            }}
          >
            {/* Widget Header */}
            <div 
              className="combined-widget-header"
              style={{ borderColor: `${mergedColorPalette.secondary}15` }}
            >
              <div className="combined-mode-buttons">
                <button
                  onClick={() => handleModeSwitch('voice')}
                  className={`combined-mode-button ${activeMode === 'voice' ? 'active' : ''}`}
                  style={{ backgroundColor: mergedColorPalette.background }}
                >
                  <Mic 
                    className="combined-icon" 
                    style={{ color: mergedColorPalette.primary }} 
                  />
                </button>
                <button
                  onClick={() => handleModeSwitch('chat')}
                  className={`combined-mode-button ${activeMode === 'chat' ? 'active' : ''}`}
                  style={{ backgroundColor: mergedColorPalette.background }}
                >
                  <MessageCircle 
                    className="combined-icon" 
                    style={{ color: mergedColorPalette.primary }} 
                  />
                </button>
              </div>
  
              <div className="combined-header-actions">
                {activeMode === 'chat' && messages.length > 0 && (
                  <button 
                    onClick={() => {
                      setMessages([]);
                      onConversationReset?.();
                    }} 
                    className="combined-action-button"
                    style={{ backgroundColor: mergedColorPalette.background }}
                  >
                    <RefreshCw 
                      className="combined-icon" 
                      style={{ color: mergedColorPalette.primary }}
                    />
                  </button>
                )}
                <button 
                  onClick={handleWidgetClose} 
                  className="combined-action-button"
                  style={{ backgroundColor: mergedColorPalette.background }}
                >
                  <X 
                    className="combined-icon" 
                    style={{ color: mergedColorPalette.primary }} 
                  />
                </button>
              </div>
            </div>
  
            {/* Error Message */}
            {error && (
              <div 
                className="combined-error-message"
                style={{ 
                  backgroundColor: `${mergedColorPalette.accent}15`,
                  color: mergedColorPalette.accent 
                }}
              >
                {error}
              </div>
            )}
  
            {/* Content Area */}
            {activeMode === 'chat' ? (
              <ChatContent
                messages={messages}
                inputMessage={inputMessage}
                setInputMessage={setInputMessage}
                sendMessage={sendMessage}
                isLoading={isLoading}
                messagesEndRef={messagesEndRef}
                mergedColorPalette={mergedColorPalette}
                allowNavigation={allowNavigation}
                setAllowNavigation={setAllowNavigation}
                showNavigationControl={showNavigationControl}
              />
            ) : (
              <VoiceContent
                clientName={clientName}
                showContactForm={showContactForm}
                handleContactSubmit={handleContactSubmit}
                callOngoing={callOngoing}
                isInitializing={isInitializing}
                startCall={startCall}
                isMuted={isMuted}
                toggleMute={toggleMute}
                endCall={endCall}
                error={error}
                mergedColorPalette={mergedColorPalette}
                isFreeTrial={isFreeTrial.current}
                allowNavigation={allowNavigation}
                setAllowNavigation={setAllowNavigation}
                showNavigationControl={showNavigationControl}
              />
            )}
            <Footer colorPalette={mergedColorPalette} />
          </div>
        )}
      </div>
  
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showEndCallModal}
        onClose={() => {
          setShowEndCallModal(false);
          setPendingAction(null);
        }}
        onConfirm={handleConfirmAction}
        title="End Ongoing Call"
        message="You have an active call. Would you like to end it and continue?"
        confirmText="End Call"
        cancelText="Cancel"
        colorPalette={mergedColorPalette}
      />

      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(-5px);
            }
          }
          
          .widget-attention-container {
            position: relative;
            display: flex;
            justify-content: flex-end;
          }
          
          .widget-attention-message:hover {
            transform: scale(1.05);
          }
        `}
      </style>
    </>
  );
};

export default CombinedAIWidget;

export const WidgetUtils = {
  COLOR_PALETTES,
  FONT_FAMILIES
};